import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import queryString from 'query-string';
import reducers from './reducers/reducers';
import { initData } from './reducers/data';
import { initParams } from './reducers/params';

import App from './components/App/App';
import './index.css';
import i18n from './i18n';

const INVALID_EMAIL = i18n.t('email-invalid');

const countryLookup = {
  'GB': 'en-GB',
  'UK': 'en-GB',
  'CH': 'en-GB',
  'DE': 'de-DE',
  'IT': 'it-IT',
  'JE': 'en-GB',
  'FR': 'fr-FR',
  'LU': 'en-GB',
  'DK': 'en-GB',
  'NL': 'en-GB',
  'NO': 'en-GB',
  'SE': 'en-GB',
  'FI': 'en-GB',
  'AT': 'en-GB',
  'BE': 'en-GB',
  'ES': 'es-ES',
  'IE': 'en-GB',
  'PL': 'en-GB',
  'SCO': 'en-GB',
  'ENG': 'en-GB',
  'IOM': 'en-GB',
  'JER': 'en-GB',
  'GSY': 'en-GB',
  'NIR': 'en-GB',
  'WAL': 'en-GB',
  'US': 'en-US',
}

// App script in page will provide the id to mount.
// Taken from https://stackoverflow.com/questions/53562804/how-to-include-the-same-react-app-twice-on-the-same-page
window.mount = function (id) {

  const element = document.getElementById(id);

  const windowAppId = `${process.env.REACT_APP_WINDOW_APP_ID}` ? `${process.env.REACT_APP_WINDOW_APP_ID}` : 'portfolioMapper';

  // Get the params from the URL
  const searchParams = queryString.parse(window.location.search);
  initParams.unsubscribe = searchParams.unsubscribe ? true : false;
  // Get contactId if passed in query params
  initParams.contactId = searchParams.contactId ? searchParams.contactId : null;

  console.log('ContactId: ', initParams.contactId);

  initParams.wtRegion = window[windowAppId] && `${window[windowAppId].wtRegion}` && `${window[windowAppId].wtRegion}` !== 'undefined' ? `${window[windowAppId].wtRegion}` : "US";
  initParams.entityCCY = window[windowAppId] && `${window[windowAppId].entityCCY}` && `${window[windowAppId].entityCCY}` !== 'undefined' ? `${window[windowAppId].entityCCY}` : "USD";
  initParams.tenant = window[windowAppId] && `${window[windowAppId].tenant}` && `${window[windowAppId].tenant}` !== 'undefined' ? `${window[windowAppId].tenant}` : "wisdomtree";
  initParams.token = window[windowAppId] && `${window[windowAppId].token}` && `${window[windowAppId].token}` !== 'undefined' ? `${window[windowAppId].token}` : null;

  // If there is a query param for country code then look up the default isoCode for that country. Default to en-GB if there is a param for it
  const countryCodeisoCode = searchParams.countrycode && countryLookup[searchParams.countrycode.toUpperCase()] ? countryLookup[searchParams.countrycode.toUpperCase()] : 'en-GB';

  // Set up isoCode - first from the window param, then from the URL if not there
  initParams.isoCode = window[windowAppId] && `${window[windowAppId].isoCode}` && `${window[windowAppId].isoCode}` !== 'undefined' ?
    `${window[windowAppId].isoCode}` : (searchParams.countrycode ? countryCodeisoCode : "en-US");

  // Handle situation where region is not set, and rather country is passed into the URL. isoCode will be updated, and wtRegion needs 
  // to be set to 'EU'
  if (initParams.isoCode !== 'en-US' && initParams.wtRegion === 'US'){
    initParams.wtRegion = 'EU';
  }

  initParams.email = window[windowAppId] && `${window[windowAppId].clientEmail}` && `${window[windowAppId].clientEmail}` !== 'undefined' ? `${window[windowAppId].clientEmail}` : INVALID_EMAIL;

  // Use the email from page if available, otherwise use query params one
  if (initParams.email === INVALID_EMAIL) {
    initParams.email = searchParams.email ? searchParams.email : INVALID_EMAIL;
  }

  // Check that we either have email/token or email/contactId to enable to app to work
  if (initParams.email !== INVALID_EMAIL && (initParams.token || initParams.contactId)) {
    initParams.paramsValid = true;
  } else {
    initParams.paramsValid = false;
  }

  initParams.dataspanApiUrl = `${process.env.REACT_APP_API_URL}`;
  initParams.dpdApiUrl = `${process.env.REACT_APP_DPD_URL}`;
  initParams.version = `${process.env.REACT_APP_VERSION}` !== 'undefined' ? `${process.env.REACT_APP_VERSION}` : 'DEV';

  // Change the language to that specified in the page.
  i18n.changeLanguage(initParams.isoCode);

  // Store the param indicating a two column layout for standard check boxes
  initParams.twoColumnLayout = element.dataset.twocolumnlayout && element.dataset.twocolumnlayout === 'true' ? true : false;

  // Store the param specifying if the SAVE button should be shown
  initParams.showSave = element.dataset.showsave && element.dataset.showsave === 'true' ? true : false;

  // Store the param (if provided) for the subscriptions to load - this will be equivalent to the 'eloquaName' field
  initParams.loadEloquaNames = element.dataset.loadeloquanames ? JSON.parse(element.dataset.loadeloquanames) : null;

  // Store the param what style of checkbox - check, slider, square
  initParams.checkBoxStyle = element.dataset.checkboxstyle ? element.dataset.checkboxstyle : 'slider';

  // Store the param for the on color of the slider. Defaults to WT Teal
  initParams.checkColorOn = element.dataset.checkcoloron ? element.dataset.checkcoloron : '#05a9b1';

  // Store the param for the off color of the slider. Defaults to WT Gray
  initParams.checkColorOff = element.dataset.checkcoloroff ? element.dataset.checkcoloroff : '#5b6770';

  // Store the param showing or hiding the check box labels. Useful for single checks where the label will be separate
  initParams.showCheckLabel = element.dataset.showchecklabel && element.dataset.showchecklabel === 'false'? false : true;

  console.log('InitParams: ', initParams);

  // Deep copy the params to remove issues with multi apps and Redux
  const paramsCopy = JSON.parse(JSON.stringify(initParams));

  // render
  ReactDOM.render((
    <BrowserRouter>
      <Provider store={createStore(reducers, { 'params': paramsCopy, 'data': initData }, applyMiddleware(thunk))}>
        <App />
      </Provider>
    </BrowserRouter>
  ), element);

}
